import React, { useEffect, useState } from "react";
import { Form, Input, Button, Card, message, Space, Typography, Select } from "antd";
import { UserOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { registerUser, clearError } from "../../store/authSlice";
import { Link, useNavigate } from "react-router-dom";
import "./AuthForms.scss";
import Logo from "../../asset/images/logo.png";
import {
  activeCountries,
  defaultCountry,
  useEpaperCountries,
} from "../../utils/epaperPricing";

const { Title, Text } = Typography;

interface SignupFormData {
  full_name: string;
  email: string;
  phone: string;
  password: string;
  confirm_password: string;
  country?: string;
}

const SignupForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector((state) => state.auth);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [phoneValue, setPhoneValue] = useState<string>();
  // The subscriber's country (E-Paper Settings) decides their plan prices
  // and currency later on. Starts on UAE (or the first active country).
  // Without the settings the field is hidden and the server keeps its
  // default (UAE), as before.
  const { countries } = useEpaperCountries();
  const countryOptions = countries ? activeCountries(countries) : [];

  useEffect(() => {
    if (!countries || form.getFieldValue("country")) return;
    const initial = defaultCountry(countries);
    if (initial) form.setFieldsValue({ country: initial.code });
  }, [countries, form]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleSubmit = async (values: SignupFormData) => {
    try {
      const submitData = {
        ...values,
        phone: phoneValue || values.phone,
        register_mode: "epaper-signup",
      };

      const result = await dispatch(registerUser(submitData)).unwrap();
      message.success(result.message || "Registration successful!");
      form.resetFields();
      setPhoneValue("");

      // Redirect to login page after successful signup
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      // Error is handled by Redux and shown via useEffect
    }
  };

  const validatePhone = () => {
    if (!phoneValue) {
      return Promise.reject(new Error("Please enter your phone number"));
    }

    // Remove all non-digit characters to check length
    const digitsOnly = phoneValue.replace(/\D/g, "");

    // Check if phone number has proper length (minimum 10 digits for most countries)
    if (digitsOnly.length < 10) {
      return Promise.reject(
        new Error("Phone number must be at least 10 digits")
      );
    }

    // Check if phone number has maximum reasonable length
    if (digitsOnly.length > 15) {
      return Promise.reject(new Error("Phone number is too long"));
    }

    // Check if the phone number starts with valid country code format
    if (!phoneValue.startsWith("+")) {
      return Promise.reject(new Error("Please select a valid country code"));
    }

    // Additional validation for Indian numbers if starts with +91
    if (phoneValue.startsWith("+91")) {
      const indianNumber = digitsOnly.substring(2); // Remove country code (91)
      if (indianNumber.length !== 10) {
        return Promise.reject(
          new Error("Indian mobile number must be 10 digits")
        );
      }
      // Check if it starts with valid Indian mobile prefix
      if (!["6", "7", "8", "9"].includes(indianNumber.charAt(0))) {
        return Promise.reject(new Error("Invalid Indian mobile number format"));
      }
    }

    return Promise.resolve();
  };

  const validateConfirmPassword = ({ getFieldValue }: any) => ({
    validator(_: any, value: string) {
      if (!value || getFieldValue("password") === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Passwords do not match"));
    },
  });

  return (
    <div className="auth-container">
      <Card className="auth-card signup-card">
        <div className="auth-header">
          {/* <Title level={2} className="auth-title">
            സുപ്രഭാതം ഇ-പേപ്പർ
          </Title> */}
          <br />
          <img src={Logo} style={{ height: "35px", marginBottom: "10px" }} />
          <br />
          {/* <br /> */}
          <Text className="auth-subtitle">Sign in to your account</Text>
        </div>

        <Form
          form={form}
          name="signup"
          onFinish={handleSubmit}
          layout="vertical"
          size="middle"
          className="auth-form"
          scrollToFirstError
        >
          <Form.Item
            name="full_name"
            label="Full Name"
            rules={[
              { required: true, message: "Please enter your full name" },
              { min: 2, message: "Name must be at least 2 characters" },
              { max: 100, message: "Name cannot exceed 100 characters" },
            ]}
          >
            <Input
              style={{ borderRadius: "0px" }}
              placeholder="Enter your full name"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            // rules={[
            //   { required: true, message: "Please enter your email address" },
            //   { type: "email", message: "Please enter a valid email address" },
            // ]}
          >
            <Input
              style={{ borderRadius: "0px" }}
              placeholder="Enter your email address"
              type="email"
            />
          </Form.Item>

          {countryOptions.length > 0 && (
            <Form.Item
              name="country"
              label="Country"
              rules={[{ required: true, message: "Please select your country" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select your country"
                options={countryOptions.map((c) => ({ value: c.code, label: c.name }))}
              />
            </Form.Item>
          )}

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ validator: validatePhone }]}
          >
            <PhoneInput
              style={{ borderRadius: "0px" }}
              placeholder="Enter phone number"
              value={phoneValue}
              onChange={setPhoneValue}
              defaultCountry="IN"
              className="phone-input"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Please enter your password" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <Input.Password
              style={{ borderRadius: "0px" }}
              prefix={<LockOutlined />}
              placeholder="Create a password"
            />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              validateConfirmPassword,
            ]}
          >
            <Input.Password
              style={{ borderRadius: "0px" }}
              prefix={<LockOutlined />}
              placeholder="Confirm your password"
            />
          </Form.Item>

          <Form.Item className="auth-submit">
            <Button
              style={{ borderRadius: "0px" }}
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              className="auth-button"
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-footer">
          <Space direction="vertical" align="center" className="w-100">
            <Text>
              Already have an account?{" "}
              <Link to="/login" className="auth-link">
                Sign In
              </Link>
            </Text>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default SignupForm;
